// Read-only queries over state shared by content, phases and the api (24.2: helpers shared by
// content files live in core). Pure; iteration in sorted id order.
import { config as defaultConfig, type Config } from '../config';
import { memoByState } from './memo';
import { original, isDraft } from 'immer';
import { committedValues, countIds, forEachSorted, sortedValues } from './sorted';
import { evaluate, mod } from './modifiers';
import type { CrimeKind, Evaluation, JusticeResponse, Modifier } from './types';
import type {
  AgeGroup,
  Building,
  BuildingTypeId,
  GameState,
  JobId,
  NeedSatisfaction,
  Person,
  PersonId,
  SeasonReport,
} from './types';

export function ageGroupOf(cfg: Config, ageSeasons: number): AgeGroup {
  if (ageSeasons < cfg.people.adultAtSeasons) return 'child';
  if (ageSeasons < cfg.people.elderAtSeasons) return 'adult';
  return 'elder';
}

export function ageYears(cfg: Config, ageSeasons: number): number {
  return Math.floor(ageSeasons / cfg.people.seasonsPerYear);
}

export function isChild(cfg: Config, p: Person): boolean {
  return p.ageSeasons < cfg.people.adultAtSeasons;
}

export function isElder(cfg: Config, p: Person): boolean {
  return p.ageSeasons >= cfg.people.elderAtSeasons;
}

/** Everyone in sorted id order. */
export function peopleSorted(state: GameState): Person[] {
  return committedValues(state.people);
}

export function population(state: GameState): number {
  return countIds(state.people);
}

/** People currently assigned to a job, sorted by id. */
export function workersIn(state: GameState, jobId: JobId): Person[] {
  return peopleSorted(state).filter((p) => p.job === jobId);
}

/** Able to work this season: assigned, not sick, injured or in custody. */
export function isWorking(p: Person): boolean {
  return (
    p.job !== 'rest' &&
    p.status.sick === null &&
    p.status.injured === null &&
    p.status.inCustody === null &&
    !p.status.quarantined
  );
}

/**
 * Buildings grouped by type in sorted id order, memoised per state. Anything that adds,
 * removes or damages a building mid-phase calls `invalidateMemo(state, BUILDING_MEMO)`.
 */
export const BUILDING_MEMO = 'buildings';

function buildingIndex(state: GameState): Map<BuildingTypeId, Building[]> {
  return memoByState(state, BUILDING_MEMO, () => {
    const index = new Map<BuildingTypeId, Building[]>();
    for (const b of sortedValues(state.buildings)) {
      const list = index.get(b.type);
      if (list === undefined) index.set(b.type, [b]);
      else list.push(b);
    }
    return index;
  });
}

export function buildingsOf(state: GameState, type: BuildingTypeId): Building[] {
  return (buildingIndex(state).get(type) ?? []).slice();
}

export function buildingCount(state: GameState, type: BuildingTypeId): number {
  return buildingIndex(state).get(type)?.length ?? 0;
}

export function hasBuilding(state: GameState, type: BuildingTypeId): boolean {
  return buildingCount(state, type) > 0;
}

/**
 * Effect strength 0..1 of a building from its condition (Section 9: "their effect drops with
 * condition"): full at or above cfg.building.fullEffectCondition, linear below.
 */
export function conditionFactor(cfg: Config, b: Building): number {
  const full = cfg.building.fullEffectCondition;
  return b.condition >= full ? 1 : Math.max(0, b.condition / full);
}

/** Sum of condition factors of all buildings of a type (e.g. effective granaries). */
export function effectiveCount(cfg: Config, state: GameState, type: BuildingTypeId): number {
  let total = 0;
  for (const b of buildingsOf(state, type)) total += conditionFactor(cfg, b);
  return total;
}

/**
 * Section 9 palisade (stream M3a): effect strength 0..1, its condition factor, and 0 unless at
 * least `guardsRequired` guards are working ("requires 2 guards to count"). Raid losses scale by
 * 1 - (1 - cfg.buildings.palisade.raidDamage) x strength (events stream).
 */
export function palisadeStrength(cfg: Config, state: GameState): number {
  // Memoised per state (Section 2: every guard's output reads it); dropped with the building
  // memo when a building changes mid-phase (ADR-0010 item 25).
  return memoByState(state, `${BUILDING_MEMO}:palisade`, () => {
    let guards = 0;
    forEachSorted(state.people, (p) => {
      if (p.job === 'guard' && isWorking(p)) guards++;
    });
    if (guards < cfg.buildings.palisade.guardsRequired) return 0;
    return Math.min(1, effectiveCount(cfg, state, 'palisade'));
  });
}

/**
 * Section 9 and 19 (stream M3a): relative chance that a fire strikes this building; 1 unless its
 * config names a `fireRisk` (the stone house 0.3x).
 */
export function fireWeight(cfg: Config, b: Building): number {
  const def = cfg.buildings[b.type];
  return 'fireRisk' in def ? def.fireRisk : 1;
}

/** Population-weighted mean trust (Section 16). */
export function settlementTrust(state: GameState): number {
  const people = peopleSorted(state);
  if (people.length === 0) return 0;
  let total = 0;
  for (const p of people) total += p.trust;
  return total / people.length;
}

export function meanHappiness(state: GameState): number {
  const people = peopleSorted(state);
  if (people.length === 0) return 0;
  let total = 0;
  for (const p of people) total += p.happiness;
  return total / people.length;
}

/** The report in progress, or the last completed one. */
export function latestReport(state: GameState): SeasonReport | null {
  return state.currentReport ?? state.history[state.history.length - 1] ?? null;
}

/** The last completed season's report. */
export function lastCompletedReport(state: GameState): SeasonReport | null {
  return state.history[state.history.length - 1] ?? null;
}

const fullNeeds: NeedSatisfaction = {
  food: 1,
  water: 1,
  shelter: 1,
  warmth: 1,
  healthCare: 1,
  safety: 1,
  belonging: 1,
};

/** This season's need satisfaction if computed yet, else last season's, else fully met. */
export function needsOf(state: GameState, personId: PersonId): NeedSatisfaction {
  return (
    state.currentReport?.needs[personId] ?? lastCompletedReport(state)?.needs[personId] ?? fullNeeds
  );
}

export function skillOf(p: Person, jobId: JobId): number {
  return jobId === 'rest' ? 0 : (p.skills[jobId] ?? 0);
}

/** Section 15: adult strength cap by age (100 to 45, then -2 per year to a floor of 50). */
export function ageStrengthCap(cfg: Config, ageSeasons: number): number {
  const years = ageYears(cfg, ageSeasons);
  const l = cfg.lifecycle;
  if (years <= l.strengthCapFullUntilYears) return cfg.people.statMax;
  return Math.max(
    l.strengthCapFloor,
    cfg.people.statMax - (years - l.strengthCapFullUntilYears) * l.strengthCapDropPerYear,
  );
}

// ---- Section 12 health queries (stream M3b). Shared by content (contagion, healer), the bodies
// phase, lifecycle (childbirth with a healer, Section 15) and the api.

/** People currently sick (injury alone does not count). */
export function sickCount(state: GameState): number {
  let n = 0;
  for (const p of peopleSorted(state)) if (p.status.sick !== null) n++;
  return n;
}

/** Sick people whose illness spreads (config.illnesses[id].contagion > 0). */
export function contagiousCount(state: GameState, cfg: Config): number {
  let n = 0;
  for (const p of peopleSorted(state)) {
    const sick = p.status.sick;
    if (sick !== null && cfg.illnesses[sick.illnessId].contagion > 0) n++;
  }
  return n;
}

/**
 * Section 12: a person healers can treat in the season `turn`: sick or injured since an earlier
 * season (an illness or injury caught this season is first treated next season).
 */
export function isPatient(p: Person, turn: number): boolean {
  const sick = p.status.sick;
  const hurt = p.status.injured;
  return (sick !== null && sick.sinceTurn !== turn) || (hurt !== null && hurt.sinceTurn !== turn);
}

/**
 * Section 12: how many people wait for treatment this season (isPatient), memoised per state
 * object (Section 2 budget; for a phase's draft it is the count at first use, ADR-0010 item 15:
 * the healers' produce hooks run before anyone new falls ill or is hurt in production).
 */
export function patientCount(state: GameState, turn: number): number {
  return memoByState(state, `patients:${turn}`, () => {
    // A draft is counted on its phase-start base: its callers (the healers in production) run
    // before anyone's health status changes in the phase, and reading the base drafts nobody.
    const people = isDraft(state) ? original(state).people : state.people;
    let n = 0;
    forEachSorted(people, (p) => {
      if (isPatient(p, turn)) n++;
    });
    return n;
  });
}

/** Section 19: a quarantine is in force in the season `turn`. */
export function quarantineActive(state: GameState, turn: number): boolean {
  const q = state.quarantine;
  return q.active && (q.untilTurn === null || q.untilTurn >= turn);
}

/** Section 19: isolated this season (contagious sick while a quarantine is in force). */
export function isIsolated(cfg: Config, state: GameState, p: Person, turn: number): boolean {
  const sick = p.status.sick;
  return (
    sick !== null && cfg.illnesses[sick.illnessId].contagion > 0 && quarantineActive(state, turn)
  );
}

function treatedThisSeason(report: SeasonReport): number {
  let n = 0;
  for (const outcome of Object.values(report.care ?? {}))
    if (outcome === 'medicine' || outcome === 'no-medicine') n++;
  return n;
}

/**
 * Section 12 and 15: whole healer slots left this season after triage (the bodies phase runs
 * before lifecycle). Visits to the healthy do not block a birth.
 */
export function healerSlotsFree(state: GameState): number {
  const report = latestReport(state);
  if (report === null) return 0;
  const slots = Math.floor((report.services.healingSlots ?? 0) + 1e-9);
  return Math.max(0, slots - treatedThisSeason(report));
}

/** Section 15: a healer can attend (childbirth risk 2% / 1% with cloth; infant 4%). */
export function hasHealerCapacity(state: GameState): boolean {
  return healerSlotsFree(state) > 0;
}

/**
 * Sections 8 and 15: children under config.lifecycle.caretakerUnderYears not covered by a
 * non-working adult parent (job rest, not in custody) or a caretaker slot (each assigned, working
 * caretaker covers config.jobs.caretaker.covers, youngest children first; counted from
 * assignments, not this season's services, so it reads the same in every phase). Uncovered
 * children take illness risk 1.5x (stream M3b) and happiness -10, parents 0.8x output (M4c).
 */
export function uncoveredUnderSix(cfg: Config, state: GameState): readonly PersonId[] {
  return memoByState(state, 'uncovered-under-six', () => {
    const limit = cfg.lifecycle.caretakerUnderYears * cfg.people.seasonsPerYear;
    const children = peopleSorted(state)
      .filter((p) => p.ageSeasons < limit)
      .sort((a, b) => a.ageSeasons - b.ageSeasons || (a.id < b.id ? -1 : 1));
    let slots = 0;
    for (const p of peopleSorted(state))
      if (p.job === 'caretaker' && isWorking(p)) slots += cfg.jobs.caretaker.covers;
    const out: PersonId[] = [];
    for (const child of children) {
      const parentHome = child.parentIds.some((id) => {
        const parent = state.people[id];
        return (
          parent !== undefined &&
          parent.ageSeasons >= cfg.people.adultAtSeasons &&
          parent.job === 'rest' &&
          parent.status.inCustody === null
        );
      });
      if (parentHome) continue;
      if (slots >= 1) {
        slots -= 1;
        continue;
      }
      out.push(child.id);
    }
    return out.sort();
  });
}

// ---- Sections 11, 15 and 18 queries (stream M3c/M4c, Stream D). Shared by content (skills,
// age contributors), the society and lifecycle phases, the steward and the api.

/**
 * Section 18 retirement age: people at or past it are not auto-assigned (they may volunteer) and
 * are not counted as idle. `never` retires nobody.
 */
export function isRetired(
  state: GameState,
  person: Person,
  cfg: Pick<Config, 'people'> = defaultConfig,
): boolean {
  const age = state.policies.retirementAge;
  if (age === 'never') return false;
  return person.ageSeasons >= age * cfg.people.seasonsPerYear;
}

/**
 * Section 11 apprenticeship: master id -> apprentices learning from them this season. An
 * apprentice counts when both work (isWorking) in the same job, the master has skill 4+ in it,
 * and the apprentice is among the master's first `maxApprentices` in id order. Memoised.
 */
export function activeApprentices(
  cfg: Config,
  state: GameState,
): Readonly<Partial<Record<PersonId, readonly PersonId[]>>> {
  return memoByState(state, 'active-apprentices', () => {
    const out: Partial<Record<PersonId, PersonId[]>> = {};
    for (const p of peopleSorted(state)) {
      if (p.masterId === null || p.masterId === p.id) continue;
      const m = state.people[p.masterId];
      if (m === undefined || m.job === 'rest' || m.job !== p.job) continue;
      if (!isWorking(p) || !isWorking(m)) continue;
      if (skillOf(m, m.job) < cfg.skills.masterMinSkill) continue;
      const list = out[m.id] ?? [];
      if (list.length >= cfg.skills.maxApprentices) continue;
      list.push(p.id);
      out[m.id] = list;
    }
    return out;
  });
}

/** Section 15: living kin in the settlement (partner, parents, children), sorted. */
export function livingKin(state: GameState, p: Person): Person[] {
  const ids = [p.partnerId, ...p.parentIds, ...p.childIds].filter(
    (id): id is PersonId => id !== null,
  );
  const out: Person[] = [];
  for (const id of [...new Set(ids)].sort()) {
    const k = state.people[id];
    if (k !== undefined) out.push(k);
  }
  return out;
}

// ---------------------------------------------------------------- Section 13 (stream M4a)
// Safety points, the safety need, detection, crime types, justice availability and raids. Pure
// queries shared by the crime phase, content (guard, justice, guard mandate, raid event), the
// consumption phase and the api. Documented for other streams in .context/requests.md.

/** The turn a crime query is about: the season in progress, or the next one between seasons. */
export function resolvingTurn(state: GameState): number {
  return state.currentReport === null ? state.turn + 1 : state.turn;
}

/** Section 8 and 13: guards on duty (assigned, able to work, strength 40+, not Gentle). */
export function guardsOnDuty(state: GameState, cfg: Config): Person[] {
  return peopleSorted(state).filter(
    (p) =>
      p.job === 'guard' &&
      isWorking(p) &&
      p.strength >= cfg.jobs.guard.minStrength &&
      !p.traits.includes('gentle'),
  );
}

/** Section 13: safety points recorded in a report (guards' output, watch posts, a manned palisade). */
export function safetyPointsOf(report: SeasonReport | null): number {
  return report?.services.safetyPoints ?? 0;
}

/**
 * Section 13: the season's safety points as production recorded them on
 * `report.services.safetyPoints`. During a season this reads the open report (0 until production
 * has run); between seasons it is the last completed season's value.
 */
export function safetyPoints(state: GameState): number {
  return safetyPointsOf(latestReport(state));
}

/** Section 6 and 13: safety points the settlement needs, population / 15. */
export function safetyNeeded(state: GameState, cfg: Config): number {
  return population(state) / cfg.threats.peoplePerSafetyPoint;
}

/** Section 13: the threats that fail the safety need outright, over the last 4 seasons. */
export function safetyThreats(
  state: GameState,
  cfg: Config,
): { undetectedKilling: boolean; raidLoss: boolean } {
  const since = resolvingTurn(state) - cfg.threats.safetyLookbackSeasons;
  const killing = state.safety.lastUndetectedKillingTurn;
  return {
    undetectedKilling: killing !== null && killing > since,
    raidLoss: state.safety.raidLosses.some(
      (r) => r.turn > since && r.fraction > cfg.threats.raidLossSafetyThreshold,
    ),
  };
}

/**
 * Section 6 and 13 safety need, 0..1 (met at 1): 0 after an undetected killing or a raid loss over
 * 10% in the last 4 seasons, otherwise safety points / (population / 15), capped at 1.
 */
export function safetyNeed(state: GameState, cfg: Config): number {
  const threats = safetyThreats(state, cfg);
  if (threats.undetectedKilling || threats.raidLoss) return 0;
  const needed = safetyNeeded(state, cfg);
  return needed <= 0 ? 1 : Math.min(1, safetyPoints(state) / needed);
}

export const CRIME_KINDS: readonly CrimeKind[] = [
  'theft-food',
  'theft-tools',
  'assault',
  'sabotage',
  'killing',
];
/** Section 5 Gentle: "never commits violent crime". */
export const VIOLENT_CRIMES: readonly CrimeKind[] = ['assault', 'killing'];

export function isTheft(crime: CrimeKind): boolean {
  return crime === 'theft-food' || crime === 'theft-tools';
}

/** Section 13 crime type weights (45 / 20 / 20 / 10 / 5%). */
export function crimeWeightOf(cfg: Config, crime: CrimeKind): number {
  switch (crime) {
    case 'theft-food':
      return cfg.crime.theftFood.weight;
    case 'theft-tools':
      return cfg.crime.theftTools.weight;
    case 'assault':
      return cfg.crime.assault.weight;
    case 'sabotage':
      return cfg.crime.sabotage.weight;
    case 'killing':
      return cfg.crime.killing.weight;
  }
}

/**
 * Section 18 guard mandate as deterrence per crime type (1 = patrol stores, the default):
 * escorting foragers leaves theft deterrence at 0.8x; deter only raises it for violent crime.
 */
export function mandateDeterrence(state: GameState, cfg: Config, crime: CrimeKind): number {
  const mandate = state.policies.guardMandate;
  if (mandate === 'escort-foragers' && isTheft(crime)) return cfg.policies.escortTheftDeterrence;
  if (mandate === 'deter' && VIOLENT_CRIMES.includes(crime))
    return cfg.policies.deterOnlyViolentDeterrence;
  return 1;
}

export interface CrimeWeight {
  crime: CrimeKind;
  /** The Section 13 table weight. */
  base: number;
  /** The weight after the guard mandate (base / mandate deterrence). */
  weight: number;
}

/**
 * Section 13 crime types a person may commit when their roll succeeds: Gentle people never
 * commit violent crime (Section 5) and killing needs trust below 25 or the Volatile trait. The
 * draw renormalises over what remains (E1-05).
 */
export function crimeTypeWeights(state: GameState, cfg: Config, p: Person): CrimeWeight[] {
  const out: CrimeWeight[] = [];
  for (const crime of CRIME_KINDS) {
    if (VIOLENT_CRIMES.includes(crime) && p.traits.includes('gentle')) continue;
    if (
      crime === 'killing' &&
      p.trust >= cfg.crime.killing.trustBelow &&
      !p.traits.includes('volatile')
    )
      continue;
    const base = crimeWeightOf(cfg, crime);
    out.push({ crime, base, weight: base / mandateDeterrence(state, cfg, crime) });
  }
  return out;
}

/** How much the guard mandate scales a person's overall crime chance (1 = no effect). */
export function mandateCrimeFactor(state: GameState, cfg: Config, p: Person): number {
  let base = 0;
  let weight = 0;
  for (const w of crimeTypeWeights(state, cfg, p)) {
    base += w.base;
    weight += w.weight;
  }
  return base > 0 ? weight / base : 1;
}

/**
 * Section 13 detection chance with its factor rows: thefts 60% + 10% per guard on duty (not
 * under "deter only", whose guards do not patrol the stores, E1-06), assault 80%, sabotage 50%,
 * killing 85%; capped at 95%.
 */
export function detectionChance(state: GameState, cfg: Config, crime: CrimeKind): Evaluation {
  const c = cfg.crime;
  const mods: Modifier[] = [];
  let base: number;
  switch (crime) {
    case 'theft-food':
    case 'theft-tools': {
      base = c.detectionBase;
      const guards = guardsOnDuty(state, cfg).length;
      if (guards > 0 && state.policies.guardMandate !== 'deter')
        mods.push(mod.add('guards:patrol', c.detectionPerGuard * guards, `${guards} guards`));
      break;
    }
    case 'assault':
      base = c.assault.detection;
      break;
    case 'sabotage':
      base = c.sabotage.detection;
      break;
    case 'killing':
      base = c.killing.detection;
      break;
  }
  mods.push(mod.min('detection:cap', c.detectionCap, 'cap'));
  return evaluate(base, mods);
}

export const JUSTICE_RESPONSES: readonly JusticeResponse[] = [
  'pardon',
  'restitution',
  'imprisonment',
  'flogging',
  'exile',
  'execution',
];
/** Section 16: the responses that establish the harsh justice norm; others watch them (-3 to -10). */
export const HARSH_RESPONSES: readonly JusticeResponse[] = ['flogging', 'exile', 'execution'];

/** Section 9 jail: 4 cells per jail, fewer as it falls into disrepair (condition factor). */
export function jailCells(state: GameState, cfg: Config): number {
  return Math.floor(cfg.buildings.jail.cells * effectiveCount(cfg, state, 'jail') + 1e-9);
}

export function prisonerCount(state: GameState): number {
  let n = 0;
  for (const p of peopleSorted(state)) if (p.status.inCustody !== null) n++;
  return n;
}

/** Section 18: execution needs a common hall and settlement trust of 60 when it is chosen. */
export function executionUnlocked(state: GameState, cfg: Config): boolean {
  return (
    hasBuilding(state, 'common-hall') && settlementTrust(state) >= cfg.policies.executionUnlockTrust
  );
}

export type JusticeBlock = 'no-jail' | 'jail-full' | 'locked';

/**
 * Why a justice response cannot be used on a case now, or null. Imprisonment needs a jail with a
 * free cell; execution needs the unlock, or to be the standing policy already (it was validated
 * when it was set, Section 18).
 */
export function responseBlock(
  state: GameState,
  cfg: Config,
  response: JusticeResponse,
): JusticeBlock | null {
  if (response === 'imprisonment') {
    if (!hasBuilding(state, 'jail')) return 'no-jail';
    return prisonerCount(state) >= jailCells(state, cfg) ? 'jail-full' : null;
  }
  if (response === 'execution') {
    return state.policies.justice.response === 'execution' || executionUnlocked(state, cfg)
      ? null
      : 'locked';
  }
  return null;
}

export interface JusticeOption {
  response: JusticeResponse;
  block: JusticeBlock | null;
}

/** Every justice response with why it is unavailable, if it is (UI and prompts). */
export function justiceOptions(state: GameState, cfg: Config = defaultConfig): JusticeOption[] {
  return JUSTICE_RESPONSES.map((response) => ({
    response,
    block: responseBlock(state, cfg, response),
  }));
}

/** Section 19 raid choices. */
export type RaidChoice = 'fight' | 'pay' | 'hide';

export interface RaidOutcome {
  choice: RaidChoice;
  /** Fractions 0..1 of the food and tool stocks the raiders take. */
  foodLoss: number;
  toolLoss: number;
  /** The larger loss; record it with a `raidLoss` delta (over 10% fails the safety need). */
  lossFraction: number;
  safetyPoints: number;
  /** Palisade strength 0..1 (0 unless manned by 2 guards; queries.palisadeStrength). */
  palisade: number;
  /** Guards who fight (Fight only), and each one's chance of injury and of death. */
  guardIds: PersonId[];
  guardInjury: number;
  guardDeath: number;
  /** Chance the raiders take a person (Fight with no guards on duty). */
  kidnap: number;
}

/**
 * Section 13 raid losses for a choice: the one rule set the raid event applies (M6). Fight:
 * up to 30% of food and tools, halved by a manned palisade, 10% less per safety point beyond 2;
 * guards risk injury (10%) and death (3%); with no guards a person may be taken. Pay: 20% of
 * food. Hide: 40% of the exposed stock (a manned palisade halves it), no injuries (E1-10).
 * `points` defaults to `safetyPoints(state)`; a choice applied before production (event-choices)
 * should pass last season's: `safetyPointsOf(lastCompletedReport(state))`.
 */
export function raidOutcome(
  state: GameState,
  cfg: Config,
  choice: RaidChoice,
  points: number = safetyPoints(state),
): RaidOutcome {
  const t = cfg.threats;
  const palisade = palisadeStrength(cfg, state);
  // Section 9 palisade "raid damage 0.5x", scaled by its strength (manned, condition).
  const palisadeMul = 1 - (1 - cfg.buildings.palisade.raidDamage) * palisade;
  let foodLoss = 0;
  let toolLoss = 0;
  let guardIds: PersonId[] = [];
  let kidnap = 0;
  switch (choice) {
    case 'fight': {
      const extra = Math.max(0, points - t.freeSafetyPoints);
      const loss =
        t.raidMaxLoss * palisadeMul * Math.max(0, 1 - t.lossReductionPerExtraPoint * extra);
      foodLoss = loss;
      toolLoss = loss;
      guardIds = guardsOnDuty(state, cfg).map((p) => p.id);
      kidnap = guardIds.length === 0 ? t.kidnapChance : 0;
      break;
    }
    case 'pay':
      foodLoss = cfg.events.raid.payFoodShare;
      break;
    case 'hide':
      foodLoss = cfg.events.raid.hideLossShare * palisadeMul;
      toolLoss = foodLoss;
      break;
  }
  const fighting = guardIds.length > 0;
  return {
    choice,
    foodLoss,
    toolLoss,
    lossFraction: Math.max(foodLoss, toolLoss),
    safetyPoints: points,
    palisade,
    guardIds,
    guardInjury: fighting ? cfg.jobs.guard.raidInjury : 0,
    guardDeath: fighting ? cfg.jobs.guard.raidDeath : 0,
    kidnap,
  };
}
